import { withGraphql } from 'src/context/GraphQlProvider'
import { FeedbackForm as FeedbackFormComponent } from 'src/components/FeedbackForm/FeedbackForm'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { Answer, Mutation, MutationUpdateFeedbackArgs, Query, QueryFeedbackArgs, Question } from 'src/types/graphql'
import { MUTATION_UPDATE_FEEDBACK, USER_FEEDBACK_QUERY } from 'src/queries/Feedback'
import { useState } from 'react'
import styled from 'styled-components'
import { GenericError } from 'src/components/Utils/GenericError/GenericError'
import { QUERY_ACTIVE_QUESTION_POOL } from 'src/queries/QuestionPool'
import { GenericLoading } from 'src/components/Utils/GenericLoading/GenericLoading'

const StyledDiv = styled.div`
    position: absolute;
    left: 50%;
    top: 40%;
    margin-top: 20px;
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
`

export const FeedbackForm = withGraphql(() => {
    const { search } = useLocation()
    const queryParams = new URLSearchParams(search)
    const data = queryParams.get('data')
    const iv = queryParams.get('iv')

    const [currentStep, setCurrentStep] = useState(1)
    const [rating, setRating] = useState(0)
    const [feedbackAnswers, setFeedbackAnswers] = useState<Answer[]>([])
    const [feedbackText, setFeedbackText] = useState('')

    const { data: qpData, loading: qpLoading, error: qpError } = useQuery<Pick<Query, 'activeQuestionPool'>, {}>(QUERY_ACTIVE_QUESTION_POOL)

    const {
        data: userFeedback,
        loading,
        error,
    } = useQuery<Pick<Query, 'feedback'>, QueryFeedbackArgs>(USER_FEEDBACK_QUERY, {
        variables: {
            encryptedUserData: { encryptedUserMessageBase64: data as string, ivHex: iv as string },
        },
        fetchPolicy: 'network-only',
        skip: !qpData?.activeQuestionPool,
    })

    const [updateFeedback] = useMutation<Pick<Mutation, 'updateFeedback'>, MutationUpdateFeedbackArgs>(MUTATION_UPDATE_FEEDBACK)

    const handleNextStep = () => {
        if (qpData?.activeQuestionPool?.questions && currentStep >= qpData.activeQuestionPool?.questions.length) {
            setCurrentStep(currentStep + 1)
            return
        }
        const answeredStep = feedbackAnswers[currentStep - 1]
        if (answeredStep) {
            let answers = [...feedbackAnswers]
            answers[currentStep - 1] = {
                category: qpData?.activeQuestionPool?.questions[currentStep - 1].category as string,
                score: rating,
            }
            setFeedbackAnswers(answers)
        } else {
            setFeedbackAnswers([
                ...feedbackAnswers,
                { score: rating, category: qpData?.activeQuestionPool?.questions[currentStep - 1].category as string },
            ])
        }
        setRating(0)
        setCurrentStep(currentStep + 1)
    }

    const handlePreviousStep = () => {
        setCurrentStep(currentStep - 1)
    }

    const onFinishFeedback = async () => {
        setCurrentStep(currentStep + 1)
        if (!userFeedback?.feedback) return

        const lastAnswerCategory = qpData?.activeQuestionPool?.questions[currentStep - 2].category || ''
        const answers: Answer[] = [...feedbackAnswers, { score: rating, category: lastAnswerCategory }]
        const totalScore = answers.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.score
        }, 0)

        const scoreAvg = totalScore / answers.length
        await updateFeedback({
            variables: {
                feedback: {
                    _id: userFeedback.feedback._id,
                    score: scoreAvg,
                    text: feedbackText,
                    answers,
                },
            },
        })
    }

    const isLoading = qpLoading || loading
    const isError = error || qpError
    const steps = qpData?.activeQuestionPool && [...qpData.activeQuestionPool.questions.map((question) => question.text)]

    return (
        <StyledDiv>
            {error && <GenericError title="Da ist was schief gelaufen!" message={error.message} />}
            {qpError && <GenericError title="Da ist was schief gelaufen!" message={qpError.message} />}

            {qpData && !qpData?.activeQuestionPool && (
                <GenericError title="Da ist was schief gelaufen!" message="Es gibt keinen aktiven Fragebogen" />
            )}

            {isLoading ? (
                <GenericLoading />
            ) : (
                !isError &&
                qpData?.activeQuestionPool && (
                    <FeedbackFormComponent
                        steps={steps || []}
                        rating={rating}
                        feedbackText={feedbackText}
                        onChangeFeedbackText={setFeedbackText}
                        onChangeRating={setRating}
                        currentStep={currentStep}
                        onNextStep={handleNextStep}
                        onPreviousStep={handlePreviousStep}
                        onFinishFeedback={onFinishFeedback}
                    />
                )
            )}
        </StyledDiv>
    )
})

export default FeedbackForm
